import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import DatePicker from '../../components/DatePicker';
import { useToast } from '../../components/Toast';
import {
  fetchHealthtronUserStats,
  fetchHealthtronUsers,
} from '../../store/slices/healthtronUserSlice';
import '../Dashboard/Dashboard.css';
import '../Customers/Customers.css';
import './Users.css';

const USERS_PER_PAGE = 10;

const EMPTY_FILTERS = { search: '', from: '', to: '', ageMin: '', ageMax: '' };

const MAX_AGE = 150;

const DEFAULT_SORT = 'newest';

const SORT_OPTIONS = [
  { value: 'newest', label: '登録日（新しい順）' },
  { value: 'oldest', label: '登録日（古い順）' },
  { value: 'balance_desc', label: '残高（高い順）' },
];

const GENDER_LABELS = {
  1: '男性',
  2: '女性',
  3: 'その他',
};

const formatSlashDate = (ymd) => (ymd ? String(ymd).replace(/-/g, '/') : '-');

const formatFullName = (first, last, separator = ' ') => {
  const fullName = [first, last].filter(Boolean).join(separator);
  return fullName || '-';
};

const getTodayYmd = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

const formatYen = (amount) => `${Number(amount || 0).toLocaleString()}円`;

const getPageNumbers = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, '...', totalPages];
  }
  if (currentPage >= totalPages - 2) {
    return [1, '...', totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, '...', currentPage, '...', totalPages];
};

const Users = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const {
    stats,
    users,
    pagination,
    isStatsLoading,
    isUsersLoading,
  } = useSelector((state) => state.healthtronUser);

  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [currentPage, setCurrentPage] = useState(1);

  const { error: toastError } = toast;
  const handleFetchError = useCallback(
    (title) => (error) => {
      if (error?.name === 'AbortError') return;
      toastError(title);
    },
    [toastError]
  );

  const { from, to, ageMin, ageMax } = appliedFilters;

  useEffect(() => {
    const request = dispatch(fetchHealthtronUserStats({ from, to, ageMin, ageMax }));
    request.unwrap().catch(handleFetchError('統計の取得に失敗しました'));
    return () => request.abort();
  }, [dispatch, from, to, ageMin, ageMax, handleFetchError]);

  useEffect(() => {
    const request = dispatch(
      fetchHealthtronUsers({ ...appliedFilters, sort, page: currentPage, perPage: USERS_PER_PAGE })
    );
    request.unwrap().catch(handleFetchError('ユーザー一覧の取得に失敗しました'));
    return () => request.abort();
  }, [dispatch, appliedFilters, sort, currentPage, handleFetchError]);

  const handleSearchChange = (e) => {
    setDraftFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  const handleAgeChange = (e) => {
    const { name, value } = e.target;
    if (value !== '' && (!/^\d+$/.test(value) || Number(value) > MAX_AGE)) return;
    setDraftFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSortChange = (e) => {
    setCurrentPage(1);
    setSort(e.target.value);
  };

  const todayYmd = getTodayYmd();

  const handleFromChange = (value) => {
    if (draftFilters.to && value > draftFilters.to) {
      toast.error('開始日は終了日より後に設定できません。');
      return;
    }
    setDraftFilters((prev) => ({ ...prev, from: value }));
  };

  const handleToChange = (value) => {
    if (draftFilters.from && value < draftFilters.from) {
      toast.error('終了日は開始日より前に設定できません。');
      return;
    }
    setDraftFilters((prev) => ({ ...prev, to: value }));
  };

  const handleApplyFilters = () => {
    const hasAgeRange = draftFilters.ageMin !== '' && draftFilters.ageMax !== '';
    if (hasAgeRange && Number(draftFilters.ageMin) > Number(draftFilters.ageMax)) {
      toast.error('年齢の下限は上限以下に設定してください。');
      return;
    }
    setCurrentPage(1);
    setAppliedFilters({ ...draftFilters, search: draftFilters.search.trim() });
  };

  const handleClearFilters = () => {
    setCurrentPage(1);
    setSort(DEFAULT_SORT);
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
  };

  const handleFilterKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    handleApplyFilters();
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.lastPage || page === currentPage) return;
    setCurrentPage(page);
  };

  const rangeLabel = stats?.range
    ? `${formatSlashDate(stats.range.from)} 〜 ${formatSlashDate(stats.range.to)}`
    : '-';

  const dailyRegistrations = (stats?.dailyRegistrations || []).map((item) => ({
    date: formatSlashDate(item.date),
    count: item.count,
  }));

  const statCards = [
    {
      label: '現在の登録者数',
      value: stats?.totalRegistered,
      hint: `退会済み: ${(stats?.deletedUsers ?? 0).toLocaleString()} 名`,
      modifier: '',
    },
    {
      label: '本日の新規登録',
      value: stats?.newToday,
      hint: '本日登録したユーザー',
      modifier: 'dashboard__stat-value--success',
    },
    {
      label: '直近7日間の新規登録',
      value: stats?.newLast7Days,
      hint: '本日を含む7日間',
      modifier: 'dashboard__stat-value--info',
    },
    {
      label: '今月の新規登録',
      value: stats?.newThisMonth,
      hint: '今月1日から本日まで',
      modifier: 'dashboard__stat-value--info',
    },
    {
      label: '期間内の新規登録',
      value: stats?.newInRange,
      hint: rangeLabel,
      modifier: 'dashboard__stat-value--warning',
    },
  ];

  const pageNumbers = getPageNumbers(currentPage, pagination.lastPage);

  return (
    <Layout>
      <div className="dashboard healthtron-users">
        <div className="dashboard__header-card">
          <div className="dashboard__title-row">
            <h1 className="dashboard__title">Healthtronユーザー</h1>
            <p className="dashboard__subtitle">
              Healthtronに登録されたユーザーの一覧と新規登録の統計。
            </p>
          </div>

          <div className="healthtron-users__filters">
            <div className="dashboard__filter-group">
              <label className="dashboard__filter-label" htmlFor="healthtron-users-search">
                キーワード
              </label>
              <input
                id="healthtron-users-search"
                type="text"
                className="customers__filter-input healthtron-users__search-input"
                placeholder="ログインID・氏名・Email・電話番号"
                value={draftFilters.search}
                onChange={handleSearchChange}
                onKeyDown={handleFilterKeyDown}
                aria-label="ユーザー検索キーワード"
              />
            </div>

            <div className="dashboard__filter-group dashboard__filter-group--date">
              <span className="dashboard__filter-label">登録日</span>
              <div className="dashboard__filter-date-range">
                <DatePicker
                  value={draftFilters.from}
                  onChange={handleFromChange}
                  maxDate={draftFilters.to || todayYmd}
                  className="dashboard__date-picker"
                />
                <span className="dashboard__filter-date-separator">〜</span>
                <DatePicker
                  value={draftFilters.to}
                  onChange={handleToChange}
                  minDate={draftFilters.from}
                  maxDate={todayYmd}
                  className="dashboard__date-picker"
                />
              </div>
            </div>

            <div className="dashboard__filter-group">
              <span className="dashboard__filter-label" id="healthtron-users-age-label">
                年齢
              </span>
              <div className="healthtron-users__age-range" role="group" aria-labelledby="healthtron-users-age-label">
                <input
                  type="text"
                  inputMode="numeric"
                  name="ageMin"
                  className="customers__filter-input healthtron-users__age-input"
                  placeholder="下限"
                  value={draftFilters.ageMin}
                  onChange={handleAgeChange}
                  onKeyDown={handleFilterKeyDown}
                  aria-label="年齢の下限"
                />
                <span className="dashboard__filter-date-separator">〜</span>
                <input
                  type="text"
                  inputMode="numeric"
                  name="ageMax"
                  className="customers__filter-input healthtron-users__age-input"
                  placeholder="上限"
                  value={draftFilters.ageMax}
                  onChange={handleAgeChange}
                  onKeyDown={handleFilterKeyDown}
                  aria-label="年齢の上限"
                />
                <span className="healthtron-users__age-unit">歳</span>
              </div>
            </div>

            <div className="dashboard__filter-group">
              <label className="dashboard__filter-label" htmlFor="healthtron-users-sort">
                並び順
              </label>
              <select
                id="healthtron-users-sort"
                className="dashboard__filter-select healthtron-users__sort-select"
                value={sort}
                onChange={handleSortChange}
                aria-label="ユーザー一覧の並び順"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="healthtron-users__filter-actions">
              <button
                type="button"
                className="customers__search-btn"
                onClick={handleApplyFilters}
                disabled={isUsersLoading}
                aria-label="検索条件を適用"
              >
                検索
              </button>
              <button
                type="button"
                className="dashboard__filter-clear"
                onClick={handleClearFilters}
                aria-label="検索条件をクリア"
              >
                クリア
              </button>
            </div>
          </div>
        </div>

        <div className="dashboard__grid">
          {statCards.map((card) => (
            <div key={card.label} className="dashboard__stat-card">
              <div className="dashboard__stat-label">{card.label}</div>
              <div className={`dashboard__stat-value ${card.modifier}`}>
                {isStatsLoading || card.value == null ? '-' : card.value.toLocaleString()}
              </div>
              <div className="dashboard__stat-hint">{card.hint}</div>
            </div>
          ))}
        </div>

        <div className="dashboard__chart-card">
          <div className="dashboard__chart-header">
            <h2 className="dashboard__chart-title">日別の新規登録数</h2>
            <span className="dashboard__chart-caption">
              {rangeLabel}（期間指定がない場合は直近30日）
            </span>
          </div>
          <div className="dashboard__chart-body">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={dailyRegistrations} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E0E7FF" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value) => [`${value} 名`, '新規登録']}
                  labelFormatter={(label) => `日付: ${label}`}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#5cb3e8"
                  strokeWidth={3}
                  dot={{ r: 4, strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="dashboard__chart-card">
          <div className="dashboard__chart-header">
            <h2 className="dashboard__chart-title">ユーザー一覧</h2>
            <span className="dashboard__chart-caption">
              該当件数: {pagination.total.toLocaleString()} 名
            </span>
          </div>

          {isUsersLoading && <Loading text="ユーザーデータを読み込み中..." />}

          {!isUsersLoading && users.length > 0 && (
            <div className="customers__table-wrapper">
              <table className="customers__table">
                <thead className="customers__table-head">
                  <tr className="customers__table-row">
                    <th className="customers__table-th">ID</th>
                    <th className="customers__table-th">ログインID</th>
                    <th className="customers__table-th">氏名</th>
                    <th className="customers__table-th">氏名（漢字）</th>
                    <th className="customers__table-th">Email</th>
                    <th className="customers__table-th">電話番号</th>
                    <th className="customers__table-th">性別</th>
                    <th className="customers__table-th">生年月日</th>
                    <th className="customers__table-th">年齢</th>
                    <th className="customers__table-th">残高</th>
                    <th className="customers__table-th">登録日時</th>
                  </tr>
                </thead>
                <tbody className="customers__table-body">
                  {users.map((user) => (
                    <tr key={user.id} className="customers__table-row">
                      <td className="customers__table-td">{user.id}</td>
                      <td className="customers__table-td">{user.userLogin || '-'}</td>
                      <td className="customers__table-td">
                        {formatFullName(user.firstName, user.lastName)}
                      </td>
                      <td className="customers__table-td">
                        {formatFullName(user.lastNameKanji, user.firstNameKanji, '')}
                      </td>
                      <td className="customers__table-td">{user.email || '-'}</td>
                      <td className="customers__table-td">{user.mobilePhone || '-'}</td>
                      <td className="customers__table-td">{GENDER_LABELS[user.gender] || '-'}</td>
                      <td className="customers__table-td">{formatSlashDate(user.dateOfBirth)}</td>
                      <td className="customers__table-td">{user.age != null ? `${user.age}歳` : '-'}</td>
                      <td className="customers__table-td">{formatYen(user.balanceTopUp)}</td>
                      <td className="customers__table-td">{formatSlashDate(user.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isUsersLoading && users.length === 0 && (
            <div className="customers__empty">
              <p>データがありません</p>
            </div>
          )}

          {!isUsersLoading && pagination.lastPage > 1 && (
            <nav className="customers__pagination" aria-label="ユーザー一覧のページ">
              <button
                type="button"
                className="customers__pagination-btn customers__pagination-btn--nav"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="前のページ"
              >
                &lt;
              </button>

              {pageNumbers.map((page, index) => (
                <button
                  type="button"
                  key={`${page}-${index}`}
                  className={`customers__pagination-btn ${
                    page === currentPage ? 'customers__pagination-btn--active' : ''
                  } ${page === '...' ? 'customers__pagination-btn--dots' : ''}`}
                  onClick={() => page !== '...' && handlePageChange(page)}
                  disabled={page === '...'}
                  aria-label={page === '...' ? undefined : `${page}ページ目`}
                  aria-current={page === currentPage ? 'page' : undefined}
                >
                  {page}
                </button>
              ))}

              <button
                type="button"
                className="customers__pagination-btn customers__pagination-btn--nav"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.lastPage}
                aria-label="次のページ"
              >
                &gt;
              </button>
            </nav>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Users;
